import MainModel from "../../../components/Models/MainModel";
import Button from "../../../components/buttons/Button";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import moment from "moment";

interface TransactionDetails {
  [key: string]: any; // Allows multiple key-value pairs of any type
}

interface ETopUpTransactionReceiptProps {
  details: TransactionDetails;
  handleClickDone: () => void;
  defaultPaymentType?: string;
  advance?: boolean;
  recharge?: boolean;
  bundleActivation?: boolean;
}

export default function ETopUpTransactionReceipt({
  details,
  handleClickDone,
  defaultPaymentType,
  advance,
  recharge,
  bundleActivation
}: ETopUpTransactionReceiptProps) {
  // Bundle activation's success response is structurally identical to a plain
  // recharge's (transaction_details/user_details/mno_details/wallet_balance) -
  // only the page title differs.
  const isRechargeShaped = recharge || bundleActivation;

  // Parse and prepare recharge data if it's a recharge transaction
  const prepareRechargeData = () => {
    if (!isRechargeShaped || !details) return {};
    
    // Create a flattened structure for display in the receipt
    return {
      date: details.transaction_details?.date || "-",
      transaction_id: details.transaction_details?.id || "-",
      type_of_transaction: details.transaction_details?.type || "Recharge",
      phone_number: `${details.country_code || ""} ${
        details.phone_number || ""
      }`,
      amount: details.amount || 0,
      mno: details.mno_details?.name || "-",
      sender_code_name: `${details.user_details?.user_code || ""} - ${
        details.user_details?.name || ""
      }`,
      transaction_status: details.transaction_details?.status || "-",
      sender_location: details.user_details?.location || "-",
      wallet_balance: details.wallet_balance || "-", // If available in the response
    };
  };

  // Use appropriate data source based on transaction type
  const displayData = isRechargeShaped ? prepareRechargeData() : details;

  // Define transaction details in the exact order for display
  const transactionDetails = advance
  ? [
      "Date",
      "Transaction ID",
      "Type of Transaction",
      "Requested Amount",
      "Recipient User Code",
      "Recipient Location",
      "Sender User Code",
      "Sender Location",
    ]
  : isRechargeShaped ? [
      "Date",
      "Transaction ID",
      "Type of Transaction",
      "Phone Number",
      "Amount",
      "MNO",
      "Sender Code & Name",
      "Transaction Status",
      "Sender Location",
      "E Top up Balance",
  ] : [
      "Date",
      "Transaction ID",
      "Type of Transaction",
      "Requested Amount",
      "Confirmed Amount",
      "Due Amount",
      "E-Topup Stock Added",
      "Variance",
      "Recipient User Code",
      "Recipient Location",
      "Sender User Code",
      "Sender Location",
      "Payment Source",
    ];

  const getDisplayValue = (label: string) => {
    if (isRechargeShaped) {
      // Handle recharge / bundle activation specific fields
      switch (label) {
        case "Date":
          return moment(displayData.date).format("DD-MM-YYYY, hh:mm A");
        case "Transaction ID":
          return displayData.transaction_id || "-";
        case "Type of Transaction":
          return displayData.type_of_transaction || (bundleActivation ? "Bundle Activation" : "Recharge");
        case "Phone Number":
          return displayData.phone_number || "-";
        case "Amount":
          return `AFA ${Number(displayData.amount).toFixed(2)}`;
        case "MNO":
          return displayData.mno || "-";
        case "Sender Code & Name":
          return displayData.sender_code_name || "-";
        case "Transaction Status":
          return displayData.transaction_status || "-";
        case "Sender Location":
          return displayData.sender_location || "-";
        case "E Top up Balance":
          return displayData.wallet_balance !== '-' ? ("AFA " + displayData.wallet_balance) : "-";
        default:
          return "-";
      }
    } else {
      // Original logic for non-recharge transactions
      switch (label) {
        case "Date":
          return moment(details.date).format("DD-MM-YYYY, hh:mm A");
        case "Requested Amount":
          return `AFA ${Number(details.requested_amount).toFixed(2)}`;
        case "Confirmed Amount":
          return `AFA ${Number(details.confirmed_amount).toFixed(2)}`;
        case "E-Topup Stock Added":
          return `AFA ${Number(details.e_topup_stock_added).toFixed(2)}`;
        case "Variance":
          return `AFA ${Number(details.variance).toFixed(2)}`;
        case "Due Amount":
          return `AFA ${Number(details.due_amount).toFixed(2)}`;
        case "Payment Source":
          return details.payment_source ?? "-";
        default:
          const fieldKey = label.toLowerCase().replace(/ /g, "_");
          return details[fieldKey] ?? "-";
      }
    }
  };

  const getPageTitle = () => {
    if (bundleActivation) return 'Bundle Activation Transaction Successful';
    if (recharge) return 'Recharge Transaction Successful';
    if (advance) return 'E Top Up Stock Transferred Successfully';
    return 'E Top Up Request Approved Successfully';
  };

  return (
    <MainModel handleClose={handleClickDone}>
      <div className="flex items-center justify-center">
        <img src="/images/success_1.svg" alt="success_1" />
      </div>

      <h1 className="text-primary_text text-heading3 text-center pt-6">
        {getPageTitle()}
      </h1>

      <div className="h-transactionPopupHight">
        <div
          className={`h-full border mt-8 rounded-lg overflow-x-hidden overflow-auto ${getFirefoxCssFix(
            "scroll"
          )}`}
        >
          {transactionDetails.map((label) => (
            <div key={label} className="flex justify-between gap-4 p-4 border-b">
              <p className="text-title2 shrink-0 whitespace-nowrap">{label}</p>
              <p className="text-title1 w-[240px] min-w-0 text-right wrap-break-word">{getDisplayValue(label)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          buttonName={"Done"}
          className={`py-2.5 rounded-[6px] cursor-pointer`}
          textSize={"text-large"}
          buttonOnclick={handleClickDone}
          testId={`done`}
          loading={false}
          buttonWidth={"w-[100px] cursor-pointer"}
          buttonHight="h-[42px]"
        />
      </div>
    </MainModel>
  );
}